import React, { useEffect, useState } from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useQueryStates, parseAsInteger } from 'nuqs'
import { useTimeClocksQuery } from 'queries/admin/timeClock'
import { useCompanyLocationsQuery } from 'queries/company/useCompanyLocationsQuery'
import { TimeClock } from '../types'

// A keyset cursor stack: each entry is the cursor used to fetch that page
// (`undefined` == page 1). Next pushes the last response's cursor; Prev pops.
type CursorStack = Array<string | undefined>

type TimeClocksContextState = {
  state: {
    companyId: number | null
    locationId: number | null
    items: TimeClock[]
    locations: ReturnType<typeof useCompanyLocationsQuery>['data']
    hasNextPage: boolean
    hasPrevPage: boolean
    pageIndex: number
    isPending: boolean
    isFetching: boolean
    isError: boolean
    error: string
    dataUpdatedAt: number
  }
  actions: {
    setCompanyId: (value: number | undefined) => void
    setLocationId: (value: number | undefined) => void
    goNextPage: () => void
    goPrevPage: () => void
    refetch: () => void
  }
}

const [Context, useTimeClocksContextValue] =
  createContext<TimeClocksContextState>({ name: 'TimeClocksContext' })

export const TimeClocksProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useQueryStates(
    {
      companyId: parseAsInteger,
      locationId: parseAsInteger,
    },
    {
      urlKeys: {
        companyId: 'company',
        locationId: 'location',
      },
    }
  )

  // Everything that defines the list except the cursor. When it changes we must
  // restart paging from page 1, because a cursor is only valid for the filters
  // it was minted under.
  const signature = JSON.stringify({
    companyId: filters.companyId,
    locationId: filters.locationId,
  })

  const [pageState, setPageState] = useState<{
    signature: string
    stack: CursorStack
  }>({ signature, stack: [undefined] })

  // On the render where filters change, treat the stack as reset immediately so
  // we never fetch with a stale cursor; the effect below syncs the state.
  const stack: CursorStack =
    pageState.signature === signature ? pageState.stack : [undefined]

  useEffect(() => {
    if (pageState.signature !== signature) {
      setPageState({ signature, stack: [undefined] })
    }
  }, [signature, pageState.signature])

  const currentCursor = stack[stack.length - 1]

  const listParams =
    filters.companyId === null
      ? null
      : {
          companyId: filters.companyId,
          locationId: filters.locationId ?? undefined,
          cursor: currentCursor,
          includeDeleted: false,
        }

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    isFetching,
    refetch,
  } = useTimeClocksQuery(listParams, {
    enabled: filters.companyId !== null,
  })

  const { data: locations } = useCompanyLocationsQuery(
    filters.companyId ?? undefined
  )

  const setCompanyId = (value: number | undefined) => {
    // Locations are company-specific, so clear the location when the company changes.
    setFilters({
      companyId: value ?? null,
      locationId: null,
    })
  }

  const setLocationId = (value: number | undefined) => {
    setFilters({ locationId: value ?? null })
  }

  if (filters.companyId !== null && isPending && !data) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  const items = data?.items ?? []
  const nextCursor = data?.nextCursor ?? null

  const value: TimeClocksContextState = {
    state: {
      companyId: filters.companyId,
      locationId: filters.locationId,
      items,
      locations,
      hasNextPage: Boolean(nextCursor),
      hasPrevPage: stack.length > 1,
      pageIndex: stack.length,
      isPending,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      dataUpdatedAt,
    },
    actions: {
      setCompanyId,
      setLocationId,
      goNextPage: () => {
        if (!nextCursor) return
        setPageState({ signature, stack: [...stack, nextCursor] })
      },
      goPrevPage: () => {
        if (stack.length <= 1) return
        setPageState({ signature, stack: stack.slice(0, -1) })
      },
      refetch: () => {
        void refetch()
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useTimeClocksContextValue }
